import { useRef } from 'react'
import { X } from 'lucide-react'
import { FilterDropdown } from '@/components/ui/FilterDropdown'
import { Button } from '@/components/ui/button'
import type { ActiveChip } from '@/hooks/use-table-filters'
import type { FilterGroups, FilterValues } from '@/lib/table-filters'

/**
 * A chip for a narrowing control that is NOT one of `groups` — the coverage
 * strip, for instance. Same rail, same treatment: the rule is that nothing may
 * narrow the table without the rail saying so, and a control outside the filter
 * groups is exactly the kind that used to slip past it.
 */
export interface ExtraFilterChip {
  /** Stable identity for the React key. */
  key: string
  /** Shown before the colon, e.g. "Coverage". */
  groupLabel: string
  /** Shown after the colon, e.g. "Unmapped". */
  label: string
  onRemove: () => void
}

export interface TableFilterBarOptions<T> {
  groups: FilterGroups<T>
  values: FilterValues
  activeChips: ActiveChip[]
  /** Chips for narrowing controls that live outside `groups`. */
  extraChips?: ExtraFilterChip[]
  onToggle: (key: string, value: string) => void
  onClear: (key: string) => void
  /** Must clear the `extraChips` controls too, not just the filter groups. */
  onClearAll: () => void
  /** Extra controls rendered after the dropdown row. */
  children?: React.ReactNode
}

/**
 * The two halves of the filter bar, handed back separately so the host can put
 * each one where it belongs.
 */
export interface TableFilterBarSlots {
  /** The dropdown triggers. Belongs inline in the toolbar row. */
  controls: React.ReactNode
  /** The active-filter chip rail. Belongs on its own full-width row BELOW the toolbar. */
  chips: React.ReactNode
}

function Chip({
  groupLabel,
  label,
  onRemove,
}: {
  groupLabel: string
  label: string
  onRemove: () => void
}) {
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-xs text-foreground">
      {groupLabel}: {label}
      <button
        type="button"
        aria-label={`Remove ${groupLabel}: ${label}`}
        onClick={onRemove}
        // The visible glyph stays 12px so it fits the 20px-tall chip, but the
        // hit area is expanded to 24x24 by a centred pseudo-element (WCAG 2.5.8
        // target size). A real 24px box would overflow the chip.
        className="relative rounded p-0.5 -mx-0.5 text-muted-foreground transition-colors hover:bg-foreground/10 hover:text-foreground after:absolute after:left-1/2 after:top-1/2 after:h-6 after:w-6 after:-translate-x-1/2 after:-translate-y-1/2 after:content-['']"
      >
        <X className="h-3 w-3" />
      </button>
    </span>
  )
}

/**
 * The filter bar for an extraction table: one `FilterDropdown` per group, plus
 * an active-filter chip rail.
 *
 * The rail is load-bearing, not decoration. Filters persist per bid, and a
 * compliance matrix is a completeness check — a silently-restored filter
 * could let someone read a short table as "all covered" while rows are
 * hidden. The rail guarantees restored state is always visible.
 *
 * **Why a hook and not one component.** The controls sit inline in the
 * toolbar, to the right of the search box; the rail describes the WHOLE table,
 * so it has to start at the toolbar's left edge, not at the first dropdown's.
 * Nested inside the controls it could only ever be indented under them, which
 * read as "these chips belong to the Category dropdown" and left dead space
 * under the search box. Two nodes, placed independently by the host, is the
 * only arrangement that keeps DOM order matching visual order — `order-last`
 * on a single wrapper would fix the picture and break focus order (WCAG 2.4.3).
 *
 * Both nodes come from ONE call so they keep sharing state: focus restoration
 * after a dismissal has to reach from the rail back to the controls.
 *
 * `hideWhenSparse` hides the trigger only; the group stays in `groups` so
 * `pruneValues` keeps its selection (a hidden group can still be filtering).
 */
export function useTableFilterBar<T>({
  groups,
  values,
  activeChips,
  extraChips = [],
  onToggle,
  onClear,
  onClearAll,
  children,
}: TableFilterBarOptions<T>): TableFilterBarSlots {
  // `hideWhenSparse` hides a trigger the user has no real choice in — but only
  // while that group is idle. A hidden group KEEPS its selection (`pruneValues`
  // spares it), so it can still be narrowing the table with its chip as the one
  // control; dismissing that chip then left no way to apply it again. A group
  // that is filtering always shows its trigger.
  const visible = Object.entries(groups).filter(
    ([key, config]) =>
      !config.hideWhenSparse || config.options.length > 1 || (values[key]?.size ?? 0) > 0
  )

  // Gated on there being anything to clear at all, not just on the dropdown
  // groups: when an outside control is the only thing narrowing the table, this
  // used to swallow the whole rail — including "Clear all".
  const hasAnything = activeChips.length > 0 || extraChips.length > 0
  const chipCount = activeChips.length + extraChips.length

  const controlsRef = useRef<HTMLDivElement>(null)
  const railRef = useRef<HTMLDivElement>(null)

  /**
   * Dismissing a chip destroys the button that was focused, and focus falls to
   * `<body>` — a keyboard user loses their place entirely, and on the last chip
   * the whole rail (including `Clear all`) goes with it.
   *
   * Runs AFTER the parent has re-rendered with the chip gone, so the "next
   * chip" is whatever survived. Falls back to the dropdown row, which always
   * has at least one trigger and is where the filter can be re-applied — the
   * rail itself is no good as a target because it may no longer exist.
   */
  const restoreFocus = (removedIndex: number) => {
    queueMicrotask(() => {
      const remaining = railRef.current?.querySelectorAll<HTMLButtonElement>(
        'button[aria-label^="Remove "]'
      )
      const next = remaining?.[Math.min(removedIndex, remaining.length - 1)]
      if (next) {
        next.focus()
        return
      }
      controlsRef.current?.querySelector('button')?.focus()
    })
  }

  const controls = (
    <>
      {/* Always mounted, even with nothing selected: a live region that appears
          at the same moment as its own content is not reliably announced, and
          "filters cleared" is exactly the transition worth hearing. It rides
          with the controls because those are the half that always renders —
          `sr-only` is absolutely positioned, so it takes no space in the row. */}
      <p role="status" aria-live="polite" className="sr-only">
        {chipCount === 0
          ? 'No filters applied'
          : `${chipCount} ${chipCount === 1 ? 'filter' : 'filters'} applied`}
      </p>

      <div ref={controlsRef} className="flex flex-wrap items-center gap-2">
        {visible.map(([key, config]) => (
          <FilterDropdown
            key={key}
            label={config.label}
            options={config.options}
            selected={values[key] ?? new Set()}
            onToggle={(value) => onToggle(key, value)}
            onClear={() => onClear(key)}
            className="h-8"
          />
        ))}
        {children}
      </div>
    </>
  )

  const chips = hasAnything ? (
    <div
      ref={railRef}
      role="group"
      aria-label="Active filters"
      className="flex flex-wrap items-center gap-1.5"
    >
      {activeChips.map((chip, index) => (
        <Chip
          key={`${chip.key}:${chip.value}`}
          groupLabel={chip.groupLabel}
          label={chip.label}
          onRemove={() => {
            onToggle(chip.key, chip.value)
            restoreFocus(index)
          }}
        />
      ))}
      {extraChips.map((chip, index) => (
        <Chip
          key={chip.key}
          groupLabel={chip.groupLabel}
          label={chip.label}
          onRemove={() => {
            chip.onRemove()
            restoreFocus(activeChips.length + index)
          }}
        />
      ))}
      <Button variant="ghost" size="sm" className="h-6 px-2 text-xs" onClick={onClearAll}>
        Clear all
      </Button>
    </div>
  ) : null

  return { controls, chips }
}
